import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  Typography,
  Grid,
  Chip,
  Button,
  Tabs,
  Tab,
  Container,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import LoaderProgress from '../components/ui/loader-progress';
import { ArrowLeft, Eye, CheckCircle2, XCircle, Clock, Shuffle } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../services/api';
import SpokiSendMessageModal from '../components/Spoki/SpokiSendMessageModal';

const getStatusColor = (status) => {
  if (!status) return 'default';
  const lower = status.toLowerCase();
  
  // Success (Green) - Positive outcomes
  if (['completato', 'confermato', 'risolto', 'pagato', 'attivo', 'success'].some(k => lower.includes(k))) return 'success';
  
  // Error (Red) - Negative outcomes or critical states
  if (['errore', 'annullato', 'bloccato', 'rifiutato', 'chiuso', 'non risponde', 'irraggiungibile'].some(k => lower.includes(k))) return 'error';
  
  // Warning (Orange) - Pending or requiring action
  if (['in attesa', 'pending', 'da verificare', 'sospeso', 'da contattare', 'richiamare', 'occupato'].some(k => lower.includes(k))) return 'warning';
  
  // Info (Blue) - Neutral or initial states
  if (['nuovo', 'bozza', 'in lavorazione', 'aperto', 'inviato', 'letto'].some(k => lower.includes(k))) return 'info';
  
  return 'default';
};

const STATUS_COLORS = {
  created: '#0ea5e9',
  opened: '#3b82f6',
  transition: '#f59e0b',
  terminated: '#ef4444',
  failed: '#b91c1c',
  closed: '#16a34a',
  default: '#6b7280'
};

const STATUS_ICONS = {
  created: Clock,
  opened: Clock,
  transition: Shuffle,
  terminated: XCircle,
  failed: XCircle,
  closed: CheckCircle2,
  default: Clock
};

function getStatusVisuals(status) {
  const key = (status || '').toLowerCase();
  const color = STATUS_COLORS[key] || STATUS_COLORS.default;
  const IconComponent = STATUS_ICONS[key] || STATUS_ICONS.default;
  const label =
    key === 'created'
      ? 'Creata'
      : key === 'opened'
      ? 'Aperta'
      : key === 'transition'
      ? 'In transizione'
      : key === 'terminated'
      ? 'Terminata'
      : key === 'failed'
      ? 'Fallita'
      : key === 'closed'
      ? 'Chiusa'
      : status || 'Senza stato';

  return { color, IconComponent, label };
}

function PercentageDonut({ value, color }) {
  return (
    <Box
      sx={{
        position: 'relative',
        display: 'inline-flex'
      }}
    >
      <CircularProgress
        variant="determinate"
        value={Math.max(0, Math.min(100, value))}
        size={56}
        thickness={5}
        sx={{
          color,
          backgroundColor: 'rgba(145, 158, 171, 0.12)',
          borderRadius: '50%'
        }}
      />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Typography
          variant="subtitle2"
          component="div"
          sx={{ fontWeight: 600, color: 'text.primary' }}
        >
          {`${Math.round(value)}%`}
        </Typography>
      </Box>
    </Box>
  );
}

const DossierTab = ({ customerId }) => {
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDossiers = async () => {
      try {
        const res = await api.get(`/dossier/customer/${customerId}`);
        setDossiers(res.data);
      } catch (error) {
        console.error('Error fetching dossiers:', error);
        toast.error('Errore nel caricamento dei dossier');
      } finally {
        setLoading(false);
      }
    };

    if (customerId) {
      fetchDossiers();
    }
  }, [customerId]);

  if (loading) {
    return <LoaderProgress size={40} />;
  }

  if (dossiers.length === 0) {
    return <Typography>Nessun dossier trovato.</Typography>;
  }

  return (
    <TableContainer component={Paper}>
      <Table aria-label="dossier table">
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Reference</TableCell>
            <TableCell>Importo Totale</TableCell>
            <TableCell>Acconto</TableCell>
            <TableCell>Data Conferimento</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="center">Azioni</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {dossiers.map((row) => (
            <TableRow key={row.id} hover>
              <TableCell>{row.id}</TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                    {row.reference}
                  </Typography>
                  {row.description && (
                    <Typography variant="caption" color="text.secondary" sx={{ 
                      maxWidth: 200, 
                      whiteSpace: 'nowrap', 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis' 
                    }}>
                      {row.description}
                    </Typography>
                  )}
                  {row.tipo_contratto && (
                    <Chip 
                      label={row.tipo_contratto} 
                      size="small" 
                      variant="outlined" 
                      sx={{ 
                        mt: 0.5, 
                        height: 20, 
                        fontSize: '0.625rem', 
                        width: 'fit-content' 
                      }} 
                    />
                  )}
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="body2">
                    {row.importo_totale ? `${Number(row.importo_totale).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })}` : '-'}
                  </Typography>
                  {row.number_installment > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      {row.number_installment} rate
                    </Typography>
                  )}
                </Box>
              </TableCell>
              <TableCell>{row.acconto ? `${Number(row.acconto).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })}` : '-'}</TableCell>
              <TableCell>{row.data_conferimento ? new Date(row.data_conferimento).toLocaleDateString() : '-'}</TableCell>
              <TableCell>
                <Chip 
                  label={row.status} 
                  color={getStatusColor(row.status)} 
                  size="small" 
                  variant="outlined" 
                  sx={{ fontWeight: 'bold' }}
                />
              </TableCell>
              <TableCell align="center">
                <IconButton onClick={() => navigate(`/dossier/${row.id}`)} color="primary" size="small">
                  <Eye size={20} />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [statusStats, setStatusStats] = useState([]);
  const [loadingStatusStats, setLoadingStatusStats] = useState(false);
  const [spokiModalOpen, setSpokiModalOpen] = useState(false);

  useEffect(() => {
    const fetchCustomer = async () => {
      try {
        const res = await api.get(`/customer/${id}`);
        setCustomer(res.data);
      } catch (error) {
        console.error('Error fetching customer details:', error);
        toast.error('Errore nel caricamento dei dettagli Customer');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchCustomer();
    }
  }, [id]);

  useEffect(() => {
    const fetchStatusStats = async () => {
      if (!customer || !customer.id) return;
      setLoadingStatusStats(true);
      try {
        const params = {
          customer_id: customer.id
        };
        const res = await api.get('/negotiation/stats/status', { params });
        setStatusStats((res.data && res.data.items) || []);
      } catch (error) {
        console.error('Error fetching negotiation status stats for customer:', error);
      } finally {
        setLoadingStatusStats(false);
      }
    };

    fetchStatusStats();
  }, [customer]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const getStatusColor = (status) => {
    if (!status) return 'default';
    const lower = status.toLowerCase();
    
    // Success (Green) - Positive outcomes
    if (['completato', 'confermato', 'risolto', 'pagato', 'attivo', 'success'].some(k => lower.includes(k))) return 'success';
    
    // Error (Red) - Negative outcomes or critical states
    if (['errore', 'annullato', 'bloccato', 'rifiutato', 'chiuso', 'non risponde', 'irraggiungibile'].some(k => lower.includes(k))) return 'error';
    
    // Warning (Orange) - Pending or requiring action
    if (['in attesa', 'pending', 'da verificare', 'sospeso', 'da contattare', 'richiamare', 'occupato'].some(k => lower.includes(k))) return 'warning';
    
    // Info (Blue) - Neutral or initial states
    if (['nuovo', 'bozza', 'in lavorazione', 'aperto', 'inviato', 'letto'].some(k => lower.includes(k))) return 'info';
    
    return 'default';
  };

  const formatKey = (key) => {
    return key
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const formatValue = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    // Check if date string
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString();
      }
    }
    if (typeof value === 'boolean') return value ? 'Sì' : 'No';
    return value;
  };

  // Define Tab Configuration
  const tabConfig = [
    {
      label: 'Anagrafica',
      fields: ['name', 'surname', 'gender', 'sesso', 'data_nascita', 'birth_date', 'citta_nascita', 'prov_nascita', 'codice_fiscale', 'fiscal_code', 'stato_coniugale', 'figli']
    },
    {
      label: 'Contatti',
      fields: ['email', 'telephone', 'pec_aziendale']
    },
    {
      label: 'Indirizzo',
      fields: ['citta_residenza', 'prov_residenza', 'strada_residenza', 'cap_residenza', 'nazione_residenza']
    },
    {
      label: 'Commerciale',
      fields: ['evaluation', 'origin', 'stato_preventivo', 'stato_questionario', 'motivo', 'obiettivo', 'note', 'description']
    },
    {
      label: 'Lavoro/Reddito',
      fields: ['professione', 'contratto', 'data_contratto', 'busta_paga', 'rata_mensile_sostenibile', 'trattenute', 'naspi', 'data_naspi', 'lavoro_coniuge']
    },
    {
      label: 'Patrimonio/Debiti',
      fields: ['immobili', 'beni_proprieta', 'situazione_debitoria', 'debiti_agenzia', 'estratto_agenzia', 'recupero_crediti', 'spese_mensili', 'spese_mediche', 'abilita_cessione', 'data_cessione']
    },
    {
      label: 'Azienda',
      fields: ['ragione_sociale', 'p_iva', 'sdi', 'info_company', 'citta_sede_legale', 'strada_sede_legale']
    },
    {
      label: 'Sistema',
      fields: ['id', 'lid', 'reference', 'active', 'soft_delete', 'created_at', 'updated_at', 'stato_comunicazione', 'status', 'contact_id', 'id_funnelix', 'rif_cluster']
    },
    {
      label: 'Dossier',
      fields: [], // Dossier will be handled with a custom component
      isCustom: true
    }
  ];

  // Helper to get fields for a specific tab
  const getFieldsForTab = (tabIndex) => {
    if (!customer) return [];
    const config = tabConfig[tabIndex];
    if (!config) return []; // Should not happen for defined tabs
    
    return Object.keys(customer).filter(key => 
      config.fields.includes(key.toLowerCase()) || 
      config.fields.some(field => key.toLowerCase().includes(field))
    );
  };

  // Helper to get remaining fields for "Altro"
  const getOtherFields = () => {
    if (!customer) return [];
    const allConfiguredFields = tabConfig.flatMap(t => t.fields);
    return Object.keys(customer).filter(key => {
      const lowerKey = key.toLowerCase();
      // Check if key is NOT matched by any configured field pattern
      return !allConfiguredFields.some(field => lowerKey === field || lowerKey.includes(field));
    });
  };

  // Calculate visible tabs based on available data
  const visibleTabs = [
    ...tabConfig.map((tab, index) => ({
      ...tab,
      originalIndex: index,
      hasData: tab.isCustom ? true : getFieldsForTab(index).length > 0
    })),
    {
      label: 'Altro',
      originalIndex: -1, // Special index for Altro
      hasData: getOtherFields().length > 0
    }
  ].filter(tab => tab.hasData); // Only show tabs with data

  // Find the correct data source for the current selected tab index
  // We need to map the UI tab index (0, 1, 2...) back to our data source
  const currentTab = visibleTabs[tabValue];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <LoaderProgress size={60} />
      </Box>
    );
  }

  if (!customer) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h6">Customer non trovato</Typography>
        <Button startIcon={<ArrowLeft />} onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Torna indietro
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button 
          startIcon={<ArrowLeft />} 
          onClick={() => navigate('/customer')}
          variant="outlined"
        >
          Torna alla lista
        </Button>
        <Typography variant="h4" component="h1" sx={{ flexGrow: 1 }}>
          Dettagli Customer: {customer.name} {customer.surname}
        </Typography>
        <Button
          variant="outlined"
          onClick={() => navigate(`/spoki/chat?customer_id=${customer.id || ''}&phone=${encodeURIComponent(customer.telephone || '')}`)}
        >
          Apri chat Spoki
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setSpokiModalOpen(true)}
        >
          Invia WhatsApp
        </Button>
      </Box>

      {loadingStatusStats && statusStats.length === 0 && (
        <Box sx={{ mb: 3 }}>
          <LoaderProgress size={50} />
        </Box>
      )}

      {!loadingStatusStats && statusStats.length > 0 && (
        <Box
          sx={{
            mb: 3,
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(1, minmax(0, 1fr))',
              sm: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(3, minmax(0, 1fr))',
              lg: 'repeat(4, minmax(0, 1fr))'
            },
            gap: 2
          }}
        >
          {statusStats.map((stat) => {
            const visuals = getStatusVisuals(stat.status);
            const IconComponent = visuals.IconComponent;
            return (
              <Card
                key={stat.status || 'no-status'}
                sx={{
                  borderRadius: 3,
                  boxShadow:
                    '0 0 2px 0 rgba(145, 158, 171, 0.16), 0 12px 24px -4px rgba(145, 158, 171, 0.16)',
                  p: 2.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    boxShadow:
                      '0 0 2px 0 rgba(145, 158, 171, 0.32), 0 16px 32px -4px rgba(145, 158, 171, 0.24)',
                    transform: 'translateY(-2px)'
                  }
                }}
                onClick={() => {
                  const params = new URLSearchParams();
                  params.set('customer_id', customer.id);
                  if (stat.status) {
                    params.set('status', stat.status);
                  }
                  window.location.href = `/negotiation?${params.toString()}`;
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        backgroundColor: `${visuals.color}1A`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <IconComponent size={18} color={visuals.color} />
                    </Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {visuals.label}
                    </Typography>
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {stat.count}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stat.percentage.toFixed(1)}% delle pratiche di questo cliente
                  </Typography>
                </Box>
                <PercentageDonut value={stat.percentage} color={visuals.color} />
              </Card>
            );
          })}
        </Box>
      )}

      <Card sx={{ boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)', borderRadius: 4 }}>
        <Box sx={{ display: 'flex', flexGrow: 1, minHeight: 500 }}>
          <Tabs 
            orientation="vertical"
            variant="scrollable"
            value={tabValue} 
            onChange={handleTabChange} 
            aria-label="customer details tabs"
            sx={{ borderRight: 1, borderColor: 'divider', minWidth: 160, flexShrink: 0 }}
          >
            {visibleTabs.map((tab, index) => (
              <Tab key={index} label={tab.label} sx={{ alignItems: 'flex-start', textAlign: 'left' }} />
            ))}
          </Tabs>

          <Box sx={{ p: 3, flexGrow: 1, width: '100%' }}>
            {/* Dynamic Tab Content */}
            {currentTab && (
              currentTab.isCustom && currentTab.label === 'Dossier' ? (
                <DossierTab customerId={customer.id} />
              ) : (
                <Grid container spacing={3}>
                  {(currentTab.originalIndex === -1 
                    ? getOtherFields() 
                    : getFieldsForTab(currentTab.originalIndex)
                  ).map(key => (
                    <Grid item xs={12} md={6} key={key}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {formatKey(key)}
                      </Typography>
                      {key === 'stato_comunicazione' ? (
                        <Chip 
                          label={customer[key]} 
                          color={getStatusColor(customer[key])} 
                          size="small" 
                          variant="outlined" 
                          sx={{ fontWeight: 'bold', mt: 0.5 }}
                        />
                      ) : (
                        <Typography variant="body1" sx={{ fontWeight: key === 'id' ? 'medium' : 'regular' }}>
                          {formatValue(customer[key])}
                        </Typography>
                      )}
                    </Grid>
                  ))}
                </Grid>
              )
            )}
          </Box>
        </Box>
      </Card>
      <SpokiSendMessageModal
        open={spokiModalOpen}
        onClose={() => setSpokiModalOpen(false)}
        defaultPhone={customer.telephone || ''}
        defaultCustomerId={customer.id}
        defaultPracticeId={null}
        defaultContactId={customer.contact_id || null}
        defaultEntityType="customer"
        defaultEntityId={customer.id}
      />
    </Container>
  );
}
